import React, { useState, useEffect } from 'react';
import { ArrowLeft, TrendingUp, TrendingDown, Clock, Zap, DollarSign, Award, Star, Gem, Crown, Sparkles, RefreshCw, ArrowRightLeft } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useBalanceVisibility } from '../contexts/BalanceVisibilityContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import CryptoIcon from '../components/CryptoIcon';
import HoldingsDisplay from '../components/HoldingsDisplay';
import EnhancedBinaryTrading from '../components/EnhancedBinaryTrading';
import EnhancedAIArbitrage from '../components/EnhancedAIArbitrage';
import EnhancedCryptoConversion from '../components/EnhancedCryptoConversion';

const API_URL = '';

const IntegratedTrade = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { balanceVisible, formatBalance } = useBalanceVisibility();
  const [activeTab, setActiveTab] = useState('binary');
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    fetchBalance();
    
    // Auto-refresh balance every 60 seconds for live price updates
    const interval = setInterval(fetchBalance, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchBalance = async () => {
    try {
      const token = localStorage.getItem('token');
      // Fetch portfolio with calculated total balance (live prices)
      const response = await axios.get(`${API_URL}/api/wallet/portfolio`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBalance(response.data);
    } catch (error) {
      console.error('Error fetching balance:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 border-b border-cyan-500/30 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-cyan-400" />
            </button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                Trading Hub
              </h1>
              <p className="text-xs md:text-sm text-gray-400 hidden sm:block">All trading operations in one place</p>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile-Friendly Balance Card Layer */}
      <div className="container mx-auto px-4 py-4">
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm border border-cyan-500/30 rounded-2xl p-4 shadow-xl shadow-cyan-500/10 mb-4">
          <HoldingsDisplay 
            showTotal={true}
            showIndividual={false}
            showLastUpdate={false}
            showRefreshButton={true}
            title="Portfolio Balance"
            compact={true}
          />
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="container mx-auto px-4">
        <div className="flex gap-2 bg-slate-900/50 p-2 rounded-xl border border-cyan-500/20 mb-6">
          <button
            onClick={() => setActiveTab('binary')}
            className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
              activeTab === 'binary'
                ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            <TrendingUp className="w-5 h-5" />
            Binary Trading
          </button>
          
          <button
            onClick={() => setActiveTab('arbitrage')}
            className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
              activeTab === 'arbitrage'
                ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            <Zap className="w-5 h-5" />
            AI Arbitrage
          </button>
          
          <button
            onClick={() => setActiveTab('conversion')}
            className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
              activeTab === 'conversion'
                ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            <ArrowRightLeft className="w-5 h-5" />
            Crypto Conversion
          </button>
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {activeTab === 'binary' && <EnhancedBinaryTrading onBalanceUpdate={fetchBalance} />}
          {activeTab === 'arbitrage' && <EnhancedAIArbitrage onBalanceUpdate={fetchBalance} />}
          {activeTab === 'conversion' && <EnhancedCryptoConversion onBalanceUpdate={fetchBalance} />}
        </div>
      </div>
    </div>
  );
};

// Binary Trading Tab Component - OLD VERSION (Replaced by EnhancedBinaryTrading)
// Removed commented code to fix syntax error

// AI Arbitrage Tab Component
const AIArbitrageTab = ({ onBalanceUpdate }) => {
  const { toast } = useToast();
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [investmentAmount, setInvestmentAmount] = useState('');
  const [myInvestments, setMyInvestments] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchPackages();
    fetchMyInvestments();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/arbitrage/packages`);
      if (response.data.success) {
        setPackages(Object.entries(response.data.packages).map(([id, data]) => ({
          id,
          ...data
        })));
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
    }
  };

  const fetchMyInvestments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/arbitrage/my-investments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setMyInvestments(response.data.investments);
      }
    } catch (error) {
      console.error('Error fetching investments:', error);
    }
  };

  const handleInvest = async () => {
    if (!selectedPackage) {
      toast({ title: 'Error', description: 'Please select a package', variant: 'destructive' });
      return;
    }

    if (!investmentAmount || parseFloat(investmentAmount) < selectedPackage.min_investment) {
      toast({ 
        title: 'Error', 
        description: `Minimum investment: $${selectedPackage.min_investment}`, 
        variant: 'destructive' 
      });
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('token');

    try {
      const response = await axios.post(
        `${API_URL}/api/arbitrage/invest`,
        {
          package_id: selectedPackage.id,
          amount: parseFloat(investmentAmount)
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        toast({
          title: 'Investment Created',
          description: `Successfully invested $${investmentAmount} in ${selectedPackage.name} package`,
        });
        setInvestmentAmount('');
        setSelectedPackage(null);
        fetchMyInvestments();
        onBalanceUpdate();
      }
    } catch (error) {
      toast({
        title: 'Investment Failed',
        description: error.response?.data?.detail || 'Failed to create investment',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const packageIcons = {
    silver: Award,
    gold: Star,
    diamond: Gem,
    platinum: Sparkles,
    vip: Crown
  };

  const packageColors = {
    silver: 'from-gray-400 to-gray-600',
    gold: 'from-yellow-400 to-amber-600',
    diamond: 'from-blue-400 to-purple-600',
    platinum: 'from-slate-300 to-slate-500',
    vip: 'from-orange-400 via-red-500 to-pink-600'
  };

  return (
    <div className="space-y-6">
      {/* Packages */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {packages.map((pkg) => {
          const Icon = packageIcons[pkg.id] || Award;
          const color = packageColors[pkg.id] || 'from-gray-400 to-gray-600';
          
          return (
            <button
              key={pkg.id}
              onClick={() => setSelectedPackage(pkg)}
              className={`p-6 rounded-2xl border-2 transition-all text-left ${
                selectedPackage?.id === pkg.id
                  ? 'border-cyan-400 bg-cyan-500/10'
                  : 'border-slate-700 hover:border-cyan-500/50 bg-slate-900/50'
              }`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-3 rounded-xl bg-gradient-to-br ${color}`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white">{pkg.name}</h3>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Investment Range</span>
                  <span className="text-white font-semibold">
                    ${pkg.min_investment.toFixed(0)} - ${pkg.max_investment.toFixed(0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Daily Return</span>
                  <span className="text-green-400 font-semibold">
                    {pkg.daily_return_min.toFixed(2)}% - {pkg.daily_return_max.toFixed(2)}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Period</span>
                  <span className="text-cyan-400 font-semibold">{pkg.period_days} days</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Investment Form */}
      {selectedPackage && (
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 border border-cyan-500/30">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-400" />
            Invest in {selectedPackage.name}
          </h3>
          
          <input
            type="number"
            value={investmentAmount}
            onChange={(e) => setInvestmentAmount(e.target.value)}
            placeholder={`Min: $${selectedPackage.min_investment} - Max: $${selectedPackage.max_investment}`}
            className="w-full px-4 py-3 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400 mb-4"
          />

          <button
            onClick={handleInvest}
            disabled={loading || !investmentAmount}
            className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white font-bold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating Investment...' : 'Invest Now'}
          </button>
        </div>
      )}

      {/* My Investments */}
      {myInvestments.length > 0 && (
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 border border-cyan-500/30">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-400" />
            My Investments ({myInvestments.length})
          </h3>
          <div className="space-y-3">
            {myInvestments.map((investment) => (
              <div key={investment.investment_id} className="p-4 bg-slate-800/50 rounded-lg border border-cyan-500/20">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-white font-semibold">{investment.package_id.toUpperCase()}</span>
                      <span className="text-cyan-400">${investment.amount}</span>
                    </div>
                    <div className="text-sm text-gray-400 mt-1">
                      Returns: ${investment.current_return?.toFixed(2) || '0.00'}
                    </div>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    investment.status === 'active' 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-gray-500/20 text-gray-400'
                  }`}>
                    {investment.status.toUpperCase()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Old components removed - using Enhanced versions now

export default IntegratedTrade;
